import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: "#0f172a",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
    paddingBottom: 12,
    marginBottom: 20,
    gap: 16,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexGrow: 1,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    objectFit: "cover",
  },
  name: {
    fontSize: 18,
    fontWeight: 700,
  },
  title: {
    marginTop: 4,
    fontSize: 10,
    color: "#64748b",
  },
  contact: {
    textAlign: "right",
    maxWidth: 220,
    color: "#475569",
    fontSize: 9,
    lineHeight: 1.5,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#1e293b",
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
    paddingBottom: 4,
    marginBottom: 8,
  },
  paragraph: {
    fontSize: 9.5,
    lineHeight: 1.5,
    color: "#334155",
  },
  listItem: {
    fontSize: 9.5,
    marginBottom: 4,
  },
  item: {
    marginBottom: 8,
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 8,
  },
  itemTitle: {
    fontSize: 10,
    fontWeight: 600,
  },
  itemMeta: {
    fontSize: 9,
    color: "#475569",
  },
  itemSub: {
    fontSize: 9,
    color: "#64748b",
  },
  muted: {
    fontSize: 9,
    color: "#94a3b8",
  },
});

const sanitizeText = (text) => {
  if (!text) return "";
  return text
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const renderParagraphs = (text) => {
  const sanitized = sanitizeText(text);
  if (!sanitized) return null;
  return sanitized.split(/\n{2,}/).map((p, index) => (
    <Text key={index} style={styles.paragraph}>
      {p}
    </Text>
  ));
};

const ResumePdfDocument = ({ data }) => {
  const {
    name,
    email,
    contactNumber,
    address,
    about,
    profilePic,
    skills = [],
    experience = [],
    education = [],
    certifications = [],
  } = data || {};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {profilePic ? (
              <Image src={profilePic} style={styles.avatar} />
            ) : null}
            <View>
              <Text style={styles.name}>{name || "Candidate"}</Text>
              <Text style={styles.title}>Candidate Profile</Text>
            </View>
          </View>
          <View style={styles.contact}>
            {email ? <Text>Email: {email}</Text> : null}
            {contactNumber ? <Text>Phone: {contactNumber}</Text> : null}
            {address ? <Text>Address: {address}</Text> : null}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>About</Text>
          {about ? (
            renderParagraphs(about)
          ) : (
            <Text style={styles.muted}>No summary added.</Text>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Skills</Text>
          {skills.length ? (
            skills.map((skill, index) => (
              <Text key={index} style={styles.listItem}>
                • {skill}
              </Text>
            ))
          ) : (
            <Text style={styles.muted}>No skills added.</Text>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Experience</Text>
          {experience.length ? (
            experience.map((item, index) => (
              <View key={index} style={styles.item}>
                <View style={styles.itemHeader}>
                  <Text style={styles.itemTitle}>{item.role || "Role"}</Text>
                  <Text style={styles.itemMeta}>{item.company || ""}</Text>
                </View>
                {item.duration ? (
                  <Text style={styles.itemSub}>{item.duration}</Text>
                ) : null}
                {item.description ? renderParagraphs(item.description) : null}
              </View>
            ))
          ) : (
            <Text style={styles.muted}>No experience added.</Text>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Education</Text>
          {education.length ? (
            education.map((item, index) => (
              <View key={index} style={styles.item}>
                <View style={styles.itemHeader}>
                  <Text style={styles.itemTitle}>
                    {item.degree || "Education"}
                  </Text>
                  <Text style={styles.itemMeta}>{item.institute || ""}</Text>
                </View>
                {item.year ? (
                  <Text style={styles.itemSub}>{item.year}</Text>
                ) : null}
              </View>
            ))
          ) : (
            <Text style={styles.muted}>No education added.</Text>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Certifications</Text>
          {certifications.length ? (
            certifications.map((item, index) => (
              <View key={index} style={styles.item}>
                <View style={styles.itemHeader}>
                  <Text style={styles.itemTitle}>
                    {item.title || "Certification"}
                  </Text>
                </View>
                {item.year ? (
                  <Text style={styles.itemSub}>{item.year}</Text>
                ) : null}
              </View>
            ))
          ) : (
            <Text style={styles.muted}>No certifications added.</Text>
          )}
        </View>
      </Page>
    </Document>
  );
};

export default ResumePdfDocument;
